
// components/layout/MobileNav.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  TrendingUp,
  Store,
  ClipboardList,
  MoreHorizontal,
  X,
  Package,
  ShoppingBag,
  User,
  Wallet,
  Settings,
  HelpCircle,
  Bell,
  LayoutDashboard,
  Users,
} from "lucide-react";
import { useTheme } from "../../hooks/useTheme";
import { useAuth } from "../../providers/AuthProvider";

const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const [showMore, setShowMore] = useState(false);

  // Close sheet on route change
  useEffect(() => {
    setShowMore(false);
  }, [location.pathname]);

  const tabs = useMemo(
    () => [
      { id: "home", label: "Home", icon: Home, path: "/" },
      { id: "data", label: "Data", icon: TrendingUp, path: "/data" },
      { id: "shop", label: "Shop", icon: Store, path: "/marketplace" },
      { id: "orders", label: "Orders", icon: ClipboardList, path: "/orders" },
    ],
    []
  );

  const moreItems = useMemo(() => {
    const base = [
      { label: "Marketplace Orders", icon: ShoppingBag, path: "/marketplace/orders" },
      { label: "Notices", icon: Bell, path: "/notices" },
      { label: "Wallet", icon: Wallet, path: "/wallet" },
      { label: "Profile", icon: User, path: "/profile" },
      { label: "Settings", icon: Settings, path: "/settings" },
      { label: "Help & Support", icon: HelpCircle, path: "/help" },
    ];
    if (user?.role === "AGENT") {
      base.unshift(
        { label: "Agent Dashboard", icon: LayoutDashboard, path: "/agent/dashboard" },
        { label: "Sub Agents", icon: Users, path: "/agent/subagents" }
      );
    } else if (user?.role === "SUB_AGENT") {
      base.unshift({
        label: "Dashboard",
        icon: LayoutDashboard,
        path: "/subagent/dashboard",
      });
    } else if (user?.role === "ADMIN") {
      base.unshift({
        label: "Admin Dashboard",
        icon: LayoutDashboard,
        path: "/admin/dashboard",
      });
    }
    return base;
  }, [user?.role]);

  const isActive = (path) =>
    location.pathname === path ||
    (path !== "/" && location.pathname.startsWith(path));

  const isMoreActive =
    !tabs.some((t) => isActive(t.path)) && isAuthenticated;

  return (
    <>
      {/* Bottom nav bar */}
      <nav
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          background: colors.glassBg,
          backdropFilter: "blur(20px) saturate(160%)",
          WebkitBackdropFilter: "blur(20px) saturate(160%)",
          borderTop: `1px solid ${colors.border}`,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            alignItems: "stretch",
            maxWidth: 560,
            margin: "0 auto",
            height: 60,
          }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = isActive(tab.path);
            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: active ? colors.btnBg : colors.secondaryText,
                  transition: "color 0.15s ease",
                  padding: 0,
                  position: "relative",
                }}
              >
                {active && (
                  <span
                    style={{
                      position: "absolute",
                      top: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 28,
                      height: 3,
                      borderRadius: "0 0 3px 3px",
                      background: colors.btnBg,
                    }}
                  />
                )}
                <Icon size={21} strokeWidth={active ? 2.4 : 2} />
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: active ? 700 : 500,
                    letterSpacing: 0.1,
                  }}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}

          <button
            onClick={() => setShowMore(true)}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: isMoreActive ? colors.btnBg : colors.secondaryText,
              padding: 0,
              position: "relative",
            }}
          >
            {isMoreActive && (
              <span
                style={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 28,
                  height: 3,
                  borderRadius: "0 0 3px 3px",
                  background: colors.btnBg,
                }}
              />
            )}
            <MoreHorizontal size={21} />
            <span
              style={{
                fontSize: 10.5,
                fontWeight: isMoreActive ? 700 : 500,
              }}
            >
              More
            </span>
          </button>
        </div>
      </nav>

      {/* More sheet */}
      {showMore && (
        <div
          onClick={() => setShowMore(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: colors.scrim,
            backdropFilter: "blur(4px)",
            zIndex: 200,
            display: "flex",
            alignItems: "flex-end",
            animation: "fadeIn 0.15s ease",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              background: colors.raisedBg,
              borderTopLeftRadius: 22,
              borderTopRightRadius: 22,
              padding: "10px 12px calc(env(safe-area-inset-bottom, 0px) + 20px)",
              maxHeight: "75vh",
              overflowY: "auto",
              animation: "sheetUp 0.22s cubic-bezier(0.22, 1, 0.36, 1)",
              boxShadow: `0 -10px 40px ${colors.shadowStrong}`,
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 2,
                background: colors.border,
                margin: "6px auto 16px",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 6px 10px",
              }}
            >
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: colors.heading,
                  letterSpacing: -0.2,
                }}
              >
                More
              </span>
              <button
                onClick={() => setShowMore(false)}
                aria-label="Close"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: colors.subtleBg,
                  border: `1px solid ${colors.border}`,
                  color: colors.text,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ display: "grid", gap: 2 }}>
              {moreItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: "12px 12px",
                      borderRadius: 12,
                      background: active ? colors.activeBg : "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: active ? colors.btnBg : colors.text,
                      fontSize: 14,
                      fontWeight: active ? 700 : 500,
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        background: active ? colors.btnBg : colors.subtleBg,
                        color: active ? colors.btnText : colors.secondaryText,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={17} />
                    </span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sheetUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default MobileNav;


